import { useRef, MutableRefObject } from "react";

/**
 * Returns a stable ref that always points to the latest version of a callback.
 *
 * ### Why stable references matter
 *
 * Callback props (e.g. `onElementReady`, `onChange`) are typically new
 * function instances on every parent render. When those callbacks appear in
 * a `useCallback` or `useEffect` dependency array, they cause the
 * downstream hook to re-fire on every render — even when nothing meaningful
 * changed. In payment components this is especially dangerous:
 *
 * - **Stale closures** — omitting the callback from deps avoids re-fires
 *   but permanently captures the first version of the function, so it
 *   never sees updated state or props.
 * - **Cascading re-creation** — including the callback in deps causes the
 *   wrapping `useCallback` to produce a new identity, which propagates
 *   down to child components and their own effects, potentially
 *   re-mounting payment iframes or re-attaching Stripe/Yuno listeners.
 * - **Fragile `useCallback` stabilizers** — wrapping a callback in
 *   `useCallback(cb, [cb])` is a no-op that gives a false sense of
 *   stability while still changing identity whenever the parent re-renders.
 *
 * `useCallbackRef` breaks this cycle: the ref object itself never changes
 * identity (safe to omit from deps), while `.current` is updated
 * synchronously on every render so it always invokes the latest callback.
 *
 * ### Usage
 *
 * ```ts
 * const onErrorRef = useCallbackRef(onError);
 *
 * const doWork = useCallback(() => {
 *   // Always calls the latest `onError` without re-creating `doWork`.
 *   onErrorRef.current?.();
 * }, []);
 * ```
 *
 * ### React 19 migration path
 *
 * This is the userland equivalent of the React 19 `useEffectEvent` RFC.
 * When the codebase upgrades to React 19+ and `useEffectEvent` is stable,
 * call sites should be migrated:
 *
 * ```ts
 * // Before (React 18)
 * const onErrorRef = useCallbackRef(onError);
 * const doWork = useCallback(() => { onErrorRef.current?.(); }, []);
 *
 * // After (React 19+)
 * const onErrorEvent = useEffectEvent(onError);
 * const doWork = useCallback(() => { onErrorEvent?.(); }, []);
 * ```
 *
 * @see https://react.dev/learn/separating-events-from-effects
 */
function useCallbackRef<T extends ((...args: never[]) => unknown) | undefined>(
  callback: T
): MutableRefObject<T> {
  const ref = useRef(callback);
  ref.current = callback;
  return ref;
}

export default useCallbackRef;
